import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Header from "@/components/Header";
import StatusBadge from "@/components/StatusBadge";
import Lightbox from "@/components/Lightbox";
import UploadDialog from "@/components/UploadDialog";
import { toast } from "sonner";
import {
  Loader2, Plus, Trash2, Send, Star, ArrowLeft, Pencil, AlertTriangle, ImageIcon, GripVertical, UploadCloud,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const MAX = 25;

export default function CollectionEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", category: "", tags: "" });
  const [lightbox, setLightbox] = useState(-1);
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/collections/${id}`);
      setC(res.data);
      setForm({
        title: res.data.title, description: res.data.description || "",
        category: res.data.category || "", tags: (res.data.tags || []).join(", "),
      });
    } catch (e) {
      toast.error("Collection not found");
      navigate("/collections");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authLoading && !user) navigate("/");
    else if (user) load();
  }, [id, user, authLoading]); // eslint-disable-line

  const editable = c && (c.status === "draft" || c.status === "rejected");

  const saveMeta = async () => {
    try {
      const res = await api.put(`/collections/${id}`, {
        title: form.title, description: form.description, category: form.category,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      });
      setC(res.data);
      setEditOpen(false);
      toast.success("Saved");
    } catch (e) { toast.error("Could not save"); }
  };

  const removeItem = async (itemId) => {
    try {
      const res = await api.delete(`/collections/${id}/items/${itemId}`);
      setC(res.data);
      toast.success("Removed");
    } catch (e) { toast.error("Could not remove"); }
  };

  const setCover = async (image_url) => {
    const res = await api.put(`/collections/${id}`, { cover_image: image_url });
    setC(res.data);
    toast.success("Cover updated");
  };

  const submit = async () => {
    try {
      const res = await api.post(`/collections/${id}/submit`);
      setC(res.data);
      toast.success("Submitted for review!");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not submit"); }
  };

  const persistOrder = async (items) => {
    setC((prev) => ({ ...prev, items }));
    try {
      await api.put(`/collections/${id}/reorder`, { order: items.map((i) => i.item_id) });
    } catch (e) { toast.error("Could not save order"); load(); }
  };

  const handleDrop = (dropIndex) => {
    setOverIndex(null);
    if (dragIndex === null || dragIndex === dropIndex) { setDragIndex(null); return; }
    const items = [...c.items];
    const [moved] = items.splice(dragIndex, 1);
    items.splice(dropIndex, 0, moved);
    setDragIndex(null);
    persistOrder(items);
  };

  const onCaptionChange = (itemId, val) => {
    setC((prev) => ({ ...prev, items: prev.items.map((it) => it.item_id === itemId ? { ...it, caption: val } : it) }));
  };

  const saveCaption = async (itemId, val) => {
    try {
      const res = await api.put(`/collections/${id}/items/${itemId}`, { caption: val });
      setC(res.data);
    } catch (e) { toast.error("Could not save caption"); }
  };

  const del = async () => {
    await api.delete(`/collections/${id}`);
    toast.success("Collection deleted");
    navigate("/collections");
  };

  if (authLoading || loading || !c) {
    return (
      <div className="min-h-screen bg-parchment">
        <Header />
        <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-brick" /></div>
      </div>
    );
  }

  const count = c.items.length;

  return (
    <div className="min-h-screen bg-parchment">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link to="/collections" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-brick mb-4">
          <ArrowLeft className="h-4 w-4" /> Back to My Collections
        </Link>

        {/* Header card */}
        <div className="bg-white border border-stoneline rounded-xl p-6 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-3xl font-serif font-bold text-ink">{c.title}</h1>
                <StatusBadge status={c.status} />
              </div>
              {c.description && <p className="text-muted-foreground mt-2 max-w-2xl">{c.description}</p>}
              <div className="flex items-center gap-2 mt-3 flex-wrap">
                {c.category && <span className="text-xs bg-stone-100 px-2 py-0.5 rounded-full">{c.category}</span>}
                {(c.tags || []).map((t) => (
                  <span key={t} className="text-xs text-brick bg-brick/5 px-2 py-0.5 rounded-full">#{t}</span>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {editable && (
                <button data-testid="edit-collection-btn" onClick={() => setEditOpen(true)} className="inline-flex items-center gap-1.5 border border-stoneline hover:border-brick text-ink px-3 py-2 rounded-full text-sm font-medium transition-colors">
                  <Pencil className="h-4 w-4" /> Edit
                </button>
              )}
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <button data-testid="delete-collection-btn" className="inline-flex items-center gap-1.5 border border-stoneline hover:border-red-400 hover:text-red-600 text-ink px-3 py-2 rounded-full text-sm font-medium transition-colors">
                    <Trash2 className="h-4 w-4" /> Delete
                  </button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete collection?</AlertDialogTitle>
                    <AlertDialogDescription>This cannot be undone.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction data-testid="confirm-delete-btn" onClick={del} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>

          {c.status === "rejected" && c.review_note && (
            <div className="mt-4 flex gap-3 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
              <AlertTriangle className="h-5 w-5 shrink-0" />
              <div><span className="font-semibold">Admin feedback:</span> {c.review_note}</div>
            </div>
          )}

          {/* Progress + actions */}
          <div className="mt-5 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-[220px] flex-1">
              <div className="h-2 flex-1 bg-stone-100 rounded-full overflow-hidden max-w-xs">
                <div className="h-full bg-brick transition-all" style={{ width: `${(count / MAX) * 100}%` }} />
              </div>
              <span data-testid="item-count" className="text-sm font-semibold text-ink">{count}/{MAX} images</span>
            </div>
            <div className="flex items-center gap-2">
              {editable && (
                <>
                  <Link to="/search" data-testid="add-images-link" className="inline-flex items-center gap-1.5 border border-brick text-brick hover:bg-brick hover:text-parchment px-4 py-2 rounded-full text-sm font-semibold transition-colors">
                    <Plus className="h-4 w-4" /> Add from archive
                  </Link>
                  <button
                    data-testid="upload-photo-btn"
                    onClick={() => setUploadOpen(true)}
                    disabled={count >= MAX}
                    className="inline-flex items-center gap-1.5 border border-stoneline hover:border-brick text-ink px-4 py-2 rounded-full text-sm font-semibold transition-colors disabled:opacity-50"
                  >
                    <UploadCloud className="h-4 w-4" /> Upload photo
                  </button>
                  <button
                    data-testid="submit-for-review-btn"
                    onClick={submit}
                    disabled={count === 0}
                    className="inline-flex items-center gap-1.5 bg-brick hover:bg-brick-dark text-parchment px-4 py-2 rounded-full text-sm font-semibold transition-colors disabled:opacity-50"
                  >
                    <Send className="h-4 w-4" /> Submit for review
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Items */}
        {count === 0 ? (
          <div className="text-center py-20 mt-6 border border-dashed border-stoneline rounded-xl bg-white/50">
            <ImageIcon className="h-12 w-12 mx-auto text-stone-300" />
            <p className="font-serif text-xl text-ink mt-4">No images yet</p>
            <p className="text-muted-foreground mt-1">Add up to {MAX} images from the Archive Nepal search — or upload your own.</p>
            <div className="mt-4 flex items-center justify-center gap-3">
              <Link to="/search" className="text-brick font-semibold hover:underline">Search the archive →</Link>
              <button data-testid="empty-upload-btn" onClick={() => setUploadOpen(true)} className="inline-flex items-center gap-1.5 border border-brick text-brick hover:bg-brick hover:text-parchment px-4 py-2 rounded-full text-sm font-semibold transition-colors">
                <UploadCloud className="h-4 w-4" /> Upload photo
              </button>
            </div>
          </div>
        ) : (
          <>
            {editable && count > 1 && (
              <p className="text-xs text-muted-foreground mt-6 mb-2 flex items-center gap-1.5">
                <GripVertical className="h-3.5 w-3.5" /> Drag images to reorder your story flow
              </p>
            )}
            <div className="masonry columns-1 sm:columns-2 md:columns-3 lg:columns-4 mt-2">
              {c.items.map((item, i) => (
                <figure
                  key={item.item_id}
                  data-testid="editor-item-card"
                  draggable={editable}
                  onDragStart={() => editable && setDragIndex(i)}
                  onDragEnter={() => editable && setOverIndex(i)}
                  onDragOver={(e) => editable && e.preventDefault()}
                  onDrop={() => editable && handleDrop(i)}
                  onDragEnd={() => { setDragIndex(null); setOverIndex(null); }}
                  className={`group relative rounded-lg overflow-hidden border bg-white shadow-sm transition-all ${
                    overIndex === i ? "border-brick ring-2 ring-brick/40" : "border-stoneline"
                  } ${dragIndex === i ? "opacity-40" : ""} ${editable ? "cursor-grab active:cursor-grabbing" : ""}`}
                >
                  <div className="relative">
                    <img src={item.image_url} alt={item.title} loading="lazy" onClick={() => setLightbox(i)} className="w-full object-cover cursor-zoom-in" />
                    {editable && (
                      <span className="absolute top-2 right-2 bg-ink/60 text-parchment p-1 rounded-md opacity-0 group-hover:opacity-100 transition-opacity">
                        <GripVertical className="h-4 w-4" />
                      </span>
                    )}
                    <span className="absolute bottom-2 left-2 bg-ink/60 text-parchment text-[10px] font-bold px-1.5 py-0.5 rounded">#{i + 1}</span>
                    {item.is_upload && (
                      <span className="absolute bottom-2 right-2 bg-brick text-parchment text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded">Your upload</span>
                    )}
                    {c.cover_image === item.image_url && (
                      <span className="absolute top-2 left-2 bg-gold text-ink text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full flex items-center gap-1">
                        <Star className="h-3 w-3 fill-ink" /> Cover
                      </span>
                    )}
                  </div>
                  <figcaption className="p-3">
                    <p className="text-sm font-medium line-clamp-2 text-ink">{item.title || "Untitled"}</p>
                    {item.date && <p className="text-xs text-muted-foreground mt-0.5">{item.date}</p>}
                    {editable ? (
                      <Textarea
                        data-testid={`caption-input-${item.item_id}`}
                        value={item.caption || ""}
                        onChange={(e) => onCaptionChange(item.item_id, e.target.value)}
                        onBlur={(e) => saveCaption(item.item_id, e.target.value)}
                        placeholder="Add a caption…"
                        rows={2}
                        className="mt-2 text-sm resize-none"
                      />
                    ) : (
                      item.caption && <p className="text-sm text-ink/80 mt-1 italic">{item.caption}</p>
                    )}
                    {editable && (
                      <div className="flex items-center gap-1.5 mt-2">
                        <button onClick={() => setCover(item.image_url)} title="Set as cover" className="text-xs inline-flex items-center gap-1 border border-stoneline hover:border-gold px-2 py-1 rounded-full text-ink">
                          <Star className="h-3 w-3" /> Cover
                        </button>
                        <button data-testid={`remove-item-${item.item_id}`} onClick={() => removeItem(item.item_id)} title="Remove" className="text-xs inline-flex items-center gap-1 border border-stoneline hover:border-red-400 hover:text-red-600 px-2 py-1 rounded-full text-ink">
                          <Trash2 className="h-3 w-3" /> Remove
                        </button>
                      </div>
                    )}
                  </figcaption>
                </figure>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Edit meta dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent data-testid="edit-collection-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Edit Collection</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><label className="text-sm font-medium">Title</label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1" /></div>
            <div><label className="text-sm font-medium">Description</label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1" /></div>
            <div><label className="text-sm font-medium">Category</label>
              <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="e.g. Portraits" className="mt-1" /></div>
            <div><label className="text-sm font-medium">Tags (comma separated)</label>
              <Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="rana, kathmandu" className="mt-1" /></div>
          </div>
          <DialogFooter>
            <button data-testid="save-meta-btn" onClick={saveMeta} className="bg-brick hover:bg-brick-dark text-parchment px-5 py-2.5 rounded-full font-semibold transition-colors">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <UploadDialog
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        collectionId={id}
        onUpdated={(updated) => setC(updated)}
        remaining={MAX - count}
      />

      {lightbox >= 0 && (
        <Lightbox
          items={c.items}
          index={lightbox}
          onClose={() => setLightbox(-1)}
          onNav={(d) => setLightbox((p) => (p + d + c.items.length) % c.items.length)}
        />
      )}
    </div>
  );
}
